<template>
  <div
    class="fit relative-position"
    :style="$attrs.style"
    :class="$attrs.class"
    @dblclick="showDialog = true"
  >
    <v-chart
      class="fit"
      :option="baseOption"
      ref="chartRef"
      v-on="$attrs"
      :autoresize="{
      throttle: 500,
      onResize: onResize as any,
    }"
      @datazoom="handleDataZoomChange"
    />
    <!-- 放大图标按钮 -->
    <q-btn
      round
      flat
      dense
      icon="fullscreen"
      class="absolute q-ma-sm"
      style="top: -15px; right: -2px"
      @click="showDialog = true"
      size="lg"
    />

    <!-- 弹窗 -->
    <q-dialog
      v-model="showDialog"
      full-width
      full-height
      maximized
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card class="column fit">
        <q-card-section class="row items-center q-pb-none">
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup size="xl" />
        </q-card-section>

        <q-card-section class="col q-pt-none">
          <v-chart
            class="fit"
            :option="baseOption"
            ref="chartRef2"
            :autoresize="true"
            @datazoom="handleDataZoomChange"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { registerTheme, use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import {
  watch,
  ref,
  provide,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import theme from "./theme.json";
import { ECharts, EChartsOption, LineSeriesOption } from "echarts";
import { generateNumbers } from "@/utils/math";
import { useQuasar } from "quasar";

const getDefaultSeriesProp = () =>
  ({
    type: "line",
    showSymbol: false,
    symbol: "circle",
    smooth: true,
    clip: true,
    large: true,
    lineStyle: {
      width: 2,
    },
    data: [],
  } as LineSeriesOption);

const defaultSeriesProp = getDefaultSeriesProp();

// echarts config
use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
]);

registerTheme("ovilia-green", theme);

provide(THEME_KEY, "ovilia-green");

interface Props {
  name?: string;
  dataZommMirrorDrag?: boolean; // dataZoom 拖动时是否同步拖动
  dataZommDisabledStart?: boolean; // dataZoom  y轴 禁止拖动 start
  dataZommXDisabledStart?: boolean; // dataZoom x轴 禁止拖动 start
  dataZommXShow?: boolean; // 是否显示 dataZoom x轴
  xAxisLength?: number; // X 轴展示的秒数
  xAxisName?: string; // X 轴展示的秒数
  yAxisName?: string; // X 轴展示的秒数
  legendTop?: number; // X 轴展示的秒数
  max: number; // y 轴最大值
  min: number; // min
  title?: any;
  legend?: any;
  series: any[]; // series
  tooltip?: any; // tooltip
  yAxis?: any; // yAxis
  dataZoom?: any; // dataZoom
  needXDataZoom?: boolean; // dataZoom
  grid?: any; // grid
  xAxis?: any; // xAxis
  start?: number; //
  end?: number; //
  showXAxisLabel?: boolean; // X 轴展示的 label
  isInterval?: boolean; //x轴是否为整数显示
}
const props = withDefaults(defineProps<Props>(), {
  dataZommMirrorDrag: true,
  dataZommDisabledStart: false,
  dataZommXDisabledStart: false,
  dataZommXShow: true,
  needXDataZoom: false,
  xAxisLength: 15,
  showXAxisLabel: true,
  xAxisName: "",
  yAxisName: "",
  legendTop: 30,
});

const emit = defineEmits(["dataZoom"]);

defineOptions({
  inheritAttrs: false,
});
// auasar
const $q = useQuasar();

const showDialog = ref(false);

const chartRef = ref<ECharts>();
const chartRef2 = ref<ECharts>();
const changeIndex = ref(0); // 计数器

const setOption = (option: EChartsOption) => {
  chartRef.value?.setOption?.(option);
  // chartRef2 绘制
  const seriesList = props.series.map((item) => ({
    ...defaultSeriesProp,
    ...item,
  }));
  chartRef2.value?.setOption?.({ ...option, series: seriesList });
};

// watch(showDialog, (value) => {
//   if (value) {
//     nextTick(() => {
//       console.log(
//         "[BOWEN_LOG] 🚀 ~~ nextTick ~~ chartRef2.value:",
//         chartRef2.value
//       );
//       const seriesList = props.series.map((item) => ({
//         ...defaultSeriesProp,
//         ...item,
//       }));
//       // chartRef2.value?.setOption?.({ series: seriesList });
//     });
//   }
// });

watch(
  () => props.series,
  (value) => {
    // console.log("[BOWEN_LOG] 🚀 ~~  chartRef2.value:",  chartRef2.value);
    let seriesList = value || [];
    changeIndex.value++;
    if (changeIndex.value === 1) {
      setTimeout(() => {
        seriesList = seriesList.map((item) => ({
          ...defaultSeriesProp,
          ...item,
        }));
        setOption?.({
          series: seriesList || [],
        });
      }, 200);
      return;
    }

    // 每两帧数据 绘制一次
    if (changeIndex.value % 2 === 0) {
      setOption?.({
        series: seriesList || [],
      });
    }
  },
  { deep: true, immediate: true }
);

const baseOption = computed(() => {
  const xAxisDate = generateNumbers(props.xAxisLength).reverse();
  const max = props.max;
  const min = props.min;
  const start = props.start ?? props.max;
  const end = props.end ?? props.min;
  // const seriesList = props.series || [];
  // seriesList = seriesList?.map((item: any) => ({
  //   ...getDefaultSeriesProp(),
  //   ...item,
  // }));
  const lineColor = $q.dark.isActive ? "#bdbdbd" : "#032f2b";
  const textColor = "#bdbdbd";
  const lineType = [2, 8];
  const interval = (index: number, value: string) => {
    // console.log("[BOWEN_LOG] 🚀 ~~ interval ~~ index:", index, "value:", value, isInterval);
    return Number.isInteger(Number(value)) ? value : "";
  };
  const yAxisOption: any = {
    name: props.yAxisName,
    type: "value",
    max,
    min,

    interval: props.isInterval === true ? 1 : undefined, // 当 index 为 1 时，设置 interval 为 1
    minorSplitLine: {
      show: false,
      lineStyle: {
        color: textColor,
      },
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: textColor,
        type: lineType,
      },
    },
    axisLine: {
      lineStyle: {
        color: lineColor,
        type: "solid",
      },
    },
    axisLabel: {
      color: lineColor,
    },
  } as EChartsOption["yAxis"];
  const dataZoomOption: any = {
    // rangeMode: "percent",
    // backgroundColor: "#8e96a1",
    fillerColor: "#8e96a1",
    borderRadius: 5,
    moveHandleSize: 10,
    moveHandleStyle: {
      color: "#8e96a1",
    },
    brushStyle: {
      color: "#5aac5d",
      opacity: 0.7,
      borderWidth: 2,
      borderColor: "#5aac5d",
    },
    handleIcon:
      "path://M12 2C17.52 2 22 6.48 22 12S17.52 22 12 22 2 17.52 2 12 6.48 2 12 2zm0 20C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z",
    handleSize: 35,
    handleStyle: {
      color: "rgba(0,0,0,1)",
      borderColor: "#8e96a1",
      borderWidth: 0,
      backgroundColor: "#8e96a1",
      opacity: 0.7,
    },
    backgroundColor: "rgba(0,0,0,0.0)",
  };
  const options = {
    title: props.title,
    backgroundColor: "rgba(0,0,0,0.0)",
    animation: false,
    legend: {
      textStyle: {
        color: lineColor,
      },
      show: true,
      // type: 'scroll',
      orient: "vertical",
      right: 60,
      top: props.legendTop,
      // bottom: 20,
    },
    tooltip: {
      show: true,
      trigger: "axis",
      axisPointer: {
        type: "cross",
      },
    },
    // axisPointe: {
    //   show: true,
    //   type: "cross",
    //   snap:true
    // },
    grid: {
      top: 30,
      left: 50,
      right: 60,
      bottom: 40,
    },
    xAxis: {
      name: props.xAxisName,
      data: xAxisDate,
      nameTextStyle: {
        color: "#000",
      },
      // nameGap: -30,
      // minorTick: {
      //   show: true,
      // },
      // minorSplitLine: {
      //   show: true,
      //   lineStyle: {
      //     color: lineColor,
      //   },
      // },
      // axisPointer: {
      //   // snap: true,
      //   lineStyle: {
      //     color: "#8e96a1",
      //     width: 2,
      //   },
      //   label: {
      //     show: false,
      //     // formatter: function (params) {
      //     //   return echarts.format.formatTime('yyyy-MM-dd', params.value);
      //     // },
      //     backgroundColor: "#3fafa5",
      //   },
      //   handle: {
      //     show: false,
      //     color: "#3fafa5",
      //     size: 30,
      //     margin: 10,
      //     shadowOffsetX: 0,
      //     shadowOffsetY: 0,
      //     shadowBlur: 0,
      //   },
      // },
      splitLine: {
        show: true,
        interval: (index: number, value: string) => {
          return Number(value) % 1 === 0 || Number(value) === 1;
        },
        lineStyle: {
          color: textColor,
          type: lineType,
          // type: "dashed",
        },
      },
      axisLabel: {
        color: lineColor,
        show: props.showXAxisLabel, // 隐藏 x 轴标签
        interval,

        // formatter: (number: string) => {
        //   console.log("[BOWEN_LOG] 🚀 ~~ getChartsDefaultOptions ~~ number:", number);
        //   return Number(number) % 5 === 0 || Number(number) === 1 ? number : "";
        // },
      },
      axisTick: {
        interval,
      },
      axisLine: {
        onZero: false,
        lineStyle: {
          color: lineColor,
          type: "solid",
        },
      },
    },
    yAxis: {
      ...yAxisOption,
    },
    dataZoom: [
      // {
      //   id: "dataZoomY",
      //   type: "inside",
      //   yAxisIndex: [0],
      //   filterMode: "none",
      //   // minSpan: 50,
      //   // maxSpan: 100,
      //   startValue: start,
      //   endValue: end,
      // },
      {
        id: "dataZoomY",
        type: "slider",
        yAxisIndex: [0],
        filterMode: "none",
        right: 20,
        width: 20,
        ...dataZoomOption,
        startValue: start,
        endValue: end,
        top: 50,
        bottom: 40,
      },
    ],
    series: [],
  } as EChartsOption;
  if (props.tooltip) options.tooltip = props.tooltip;
  if (props.yAxis) options.yAxis = props.yAxis;
  if (props.needXDataZoom)
    options.dataZoom = [
      ...(options.dataZoom as any),
      {
        id: "dataZoomX",
        type: "slider",
        show: props.dataZommXShow,
        xAxisIndex: [0],
        filterMode: "none",
        ...dataZoomOption,
        height: 20,
        bottom: 10,
        moveHandleSize: 0,
      },
    ];
  if (props.dataZoom) options.dataZoom = props.dataZoom;
  if (props.grid)
    options.grid = {
      ...options.grid,
      ...props.grid,
    };
  if (props.xAxis) options.xAxis = props.xAxis;
  if (props.legend) options.legend = props.legend;
  return options as EChartsOption;
});

const handleDataZoomChange = (value: any) => {
  emit("dataZoom", value);
  if (props.dataZommMirrorDrag && value.dataZoomId === "dataZoomY") {
    const { end } = value;
    const start = 100 - end;
    setOption({
      dataZoom: [
        {
          start: start,
        },
      ],
    });
  } else if (props.dataZommDisabledStart && value.dataZoomId === "dataZoomY") {
    const { end, start } = value;
    const option = { dataZoom: [{ end, start: 0 }] };
    setOption(option);
  }
  if (props.dataZommXDisabledStart && value.dataZoomId === "dataZoomX") {
    const { end, start } = value;
    const option = { dataZoom: [{}, { end, start: 0 }] };
    setOption(option);
  }
};
const onResize = (e: any) => {
  console.log("[BOWEN_LOG] 🚀 ~~ onResize ~~ e:", e);
  if (!chartRef?.value) return;
  // chartRef?.value?.resize?.();
};

onMounted(() => {
  // onResize();
  // window.addEventListener("resize", onResize);
  // chartRef.value?.on?.("finished", function () {
  //   MathJax.typeset();
  // });
});

onUnmounted(() => {
  // window.removeEventListener("resize", onResize);
  // chartRef.value?.off?.("finished");
});

defineExpose({
  echarts: chartRef,
});
</script>

<style scoped>
.chart {
  height: 100%;
  width: 100%;
  position: relative;
}
.full-height {
  height: 100%;
}
</style>
